import { useEffect, useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import FormField from "../forms/FormField";
import { TailwindSpinner } from "../Spinner/Spinner";
import { Form, Formik } from "formik";
import {
  useDownloadCallCenterReport,
  useGetCallCenterQuery,
  useGetCallCenterReport,
  useGetCallCenterStatus,
} from "../../hooks/Query/useGetAllCallCenterQuery";
import MemberEnrollmentTable from "../MemberEnrolements/MemberEnrolmentTable";
import {
  CallCenterReportTable,
  RedeemptionReportColumn,
} from "../Table/TableColums";
import { DownloadModal } from "../DownloadModal";
import { toast } from "react-toastify";
import { downloadFile } from "../../utils/downloadFile";
import { FomatText } from "../../utils/formatText";

const validationSchema = Yup.object({
  fromDate: Yup.date().required("From date is required"),
  tillDate: Yup.date().required("Till date is required"),
  queryType: Yup.string().required("Query type is required"),
  reportType: Yup.string().required("Report type is required"),
  queryStatus: Yup.string().when("reportType", {
    is: "query",
    then: (schema) => schema.required("Query status is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  membership_id: Yup.string().when("reportType", {
    is: "interaction",
    then: (schema) => schema.required("Membership ID is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  limit: Yup.string(),
});

const initialValues = {
  fromDate: "",
  tillDate: "",
  queryType: "",
  queryStatus: "",
  reportType: "query",
  membership_id:"",
  limit: "",
};
export default function CallCenterReport() {
  const [showDownloadOption, setShowDownloadOption] = useState(false);
  const [payloadData, setPayloadData] = useState();
  const [queryData, setQueryData] = useState();
  const [queryStatusData, setQueryStatusData] = useState();
  const [page, setPage] = useState(1);
  const [downloadPayload, setDownloadPayload] = useState();
  const [formData, setFormData] = useState(initialValues);
  const [downloadFormat, setDownloadFormat] = useState("");
  const [shouldDownload, setShouldDownload] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const formikRef = useRef();

  //   const loading = false;
  const isLoading = false;

  // GET ALL QUERY TYPES
  const { data: queryTypes, isLoading: loadingTypes } = useGetCallCenterQuery();
  useEffect(() => {
    if (!loadingTypes && queryTypes?.data) {
      setQueryData(queryTypes?.data?.data);
    }
  }, [queryTypes, loadingTypes]);

  const { data: queryStatus, isLoading: loadingStatus } =
    useGetCallCenterStatus();
  useEffect(() => {
    if (!loadingStatus && queryStatus?.data) {
      setQueryStatusData(queryStatus?.data?.data);
    }
  }, [queryStatus, loadingStatus]);

  // DOWNLOAD CALL CENTER REPORT
  const { data: downloadData, isLoading: downloading } =
    useDownloadCallCenterReport({
      payload: downloadPayload,
    });

  useEffect(() => {
    if (shouldDownload && downloadData?.data?.data?.length > 0) {
      downloadFile({
        data: downloadData?.data?.data,
        format: downloadFormat,
        // fileName: "call_center_report",
        fileName: `FirstRewards Call Center Report ${downloadPayload?.fromDate} - ${downloadPayload?.tillDate}`,
      });

      setShouldDownload(false);
    } else if (
      shouldDownload &&
      !downloading &&
      !downloadData?.data?.data?.length
    ) {
      toast.warning("No data available to download.");
      setShouldDownload(false);
    }
  }, [
    downloadData,
    shouldDownload,
    downloadFormat,
    downloading,
    downloadPayload,
  ]);

  // GET CALL CENTER REPORT
  const {
    data: report,
    isFetching: loading,
    refetch,
  } = useGetCallCenterReport({
    payload: payloadData,
    page: page,
  });
  // Sync formData with Formik values
  useEffect(() => {
    const interval = setInterval(() => {
      if (formikRef.current?.values) {
        setFormData(formikRef.current.values);
      }
    }, 300);
    return () => clearInterval(interval);
  }, []);

  // Show download modal if limit is "All"
  useEffect(() => {
    if (formData?.limit === "All") {
      setOpenModal(true);
      setShowDownloadOption(false);
    }
  }, [formData]);

  const payload = useMemo(
    () =>
      `from_date=${formData?.fromDate}&to_date=${formData?.tillDate}&query_type_id=${formData?.queryType}&query_status=${formData?.queryStatus}`,
    [formData]
  );

  const handleShowDownloadReport = (values) => {
    if (
      values?.fromDate === "" ||
      values?.tillDate === "" ||
      values?.queryType === "" ||
      values?.queryStatus === ""
    ) {
      return toast.error("Kindly fill all required filleds.");
    } else {
      setShowDownloadOption(!showDownloadOption);
    }
  };

  const handleDownloadReport = (values, format) => {
    if (
      values?.fromDate === "" ||
      values?.tillDate === "" ||
      values?.queryType === "" ||
      values?.queryStatus === "" ||
      values?.limit === ""
    ) {
      toast.error("select a limit");
      return;
    }
    setDownloadPayload(values);
    setDownloadFormat(format);
    setShouldDownload(true);
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    refetch();
  };
  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 bg-white rounded-lg">
      <div className="mb-6">
        <h1 className="text-lg font-medium text-gray-900">
          Call Center Report
        </h1>
        <p className="text-grey-200 text-sm">
          View all call center report in the program
        </p>
      </div>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          setPayloadData(values);
        }}
      >
        {({ values, errors }) => {
          return (
            <Form className="md:w-[711px] relative h-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                <FormField
                  label="From Date"
                  name="fromDate"
                  type="date"
                  required={true}
                />
                <FormField
                  label="Till Date"
                  name="tillDate"
                  type="date"
                  required={true}
                />
                <FormField
                  label="Report Type"
                  name="reportType"
                  type="select"
                  options={[
                    {
                      value: "interactions",
                      label: "Interactions",
                    },
                    {
                      value: "query",
                      label: "Query",
                    },
                  ]}
                  required={true}
                />

                <FormField
                  label="Query Type"
                  name="queryType"
                  type="select"
                  options={queryData?.map((query) => ({
                    value: query?.Query_type_id,
                    label: FomatText(query?.Query_type_name),
                  }))}
                  required={true}
                />
                {values?.reportType === "interactions" ? (
                  <FormField
                    label="Membership ID"
                    name="membership_id"
                    type="text"
                    required={true}
                  />
                ) : (
                  <FormField
                    label="Query Status"
                    name="queryStatus"
                    type="select"
                    options={queryStatusData?.map((query) => ({
                      value: query?.Query_status,
                      label: query?.Query_status,
                    }))}
                    required={true}
                  />
                )}

                {showDownloadOption && (
                  <FormField
                    label="Download Limit"
                    name="limit"
                    type="select"
                    options={[
                      { value: "100000", label: "100,000" },
                      { value: "200000", label: "200,000" },
                      { value: "500000", label: "500,000" },
                      { value: "1000000", label: "1,000,000" },
                      { value: "All", label: "All" },
                    ]}
                  />
                )}
              </div>

              <div className="flex flex-col md:flex-row gap-4 mt-6 justify-between items-center">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-mysecondary hover:bg-blue-800 text-white px-8 py-2 rounded flex items-center justify-center w-[189px] h-[38px] text-sm"
                >
                  {loading ? <TailwindSpinner /> : "Search"}
                </button>
                <div>
                  <button
                    type="button"
                    disabled={isLoading}
                    variant="outline"
                    className=" text-center  border-1 border-mysecondary text-blue-900 hover:bg-blue-50 px-8 py-2 rounded flex items-center justify-center w-[189px] h-[38px] text-sm "
                    onClick={() => handleShowDownloadReport(values)}
                  >
                    {/* <Download className="mr-2 h-4 w-4" /> */}
                    {" Download As"}
                  </button>
                </div>
              </div>
              {showDownloadOption && (
                <div className="flex justify-center  md:justify-end ">
                  <div className="  right-0 mt-2 text-center  border-1 border-mysecondary text-blue-900 hover:bg-blue-50 rounded flex items-center  justify-end w-[189px]  flex-col">
                    <button
                      type="button"
                      disabled={isLoading}
                      className="border-b border-blue-900 w-full text-sm py-2 hover:bg-mysecondary hover:text-white flex justify-center items-center"
                      onClick={() => handleDownloadReport(values, "pdf")}
                    >
                      {isLoading && downloadFormat === "pdf" ? (
                        <TailwindSpinner />
                      ) : (
                        "Pdf"
                      )}
                    </button>
                    <button
                      type="button"
                      disabled={isLoading}
                      className="border-b border-blue-900 w-full text-sm py-2 hover:bg-mysecondary hover:text-white flex justify-center items-center"
                      onClick={() => handleDownloadReport(values, "csv")}
                    >
                      {isLoading && downloadFormat === "csv" ? (
                        <TailwindSpinner />
                      ) : (
                        "Csv"
                      )}
                    </button>
                  </div>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>

      {report?.data && report?.data?.data?.data?.length > 0 ? (
        <MemberEnrollmentTable
          columns={CallCenterReportTable}
          data={report?.data?.data?.data}
          page={page}
          setPage={setPage}
          handlePageChange={handlePageChange}
          totalPage={report?.data?.data?.total}
          loading={loading}
          title={"Call Center"}
        />
      ) : report?.data && report?.data?.data?.data?.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">
          No data available for the selected criteria.
        </div>
      ) : null}
      {openModal && (
        <DownloadModal
          setOpenModal={setOpenModal}
          value={formData}
          openModal={openModal}
          payload={payload}
        />
      )}
    </div>
  );
}
